import { Notification } from '@components/prereg_validation/notification'
import { Box, Paper, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { captureError } from '@shared/monitoring'
import {
  ColorChip,
  DEFAULT_TIMEZONE,
  formatNumberAsPrice,
  getTzDateInUtc,
  PRICE_PER_CYCLE_TEXT,
  Spacer,
} from 'components-ui'
import { formatInTimeZone } from 'date-fns-tz'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Hooks, Services, Types } from 'service-api'
import { BenefitsList } from 'components-ui/src/membership/benefits_list'
import { BenefitConsumptionTable } from 'components-ui/src/membership/benefit_consumption_table'
import { isUnlimitedBenefitsOnly } from 'components-ui/src/membership/helper'
import { useRoutes } from 'app/use_routes'

type MembershipType = 'WEEKLY' | 'FOUR_WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY'

export const Memberships = () => {
  const sessionCtx = useSessionContext()
  const { navigateRoute } = useRoutes()
  const { accountName: locationName = '' } = useParams()
  const [lupMemberships, setLupMemberships] = useState<Types.LocationUserProfileMembershipClientDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const lupId = sessionCtx.state.user!.lupId

  const { location } = useLocationContext()

  const lup = Hooks.useClientLocationUserProfile(locationName, lupId, [
    'locationUserProfileMemberships.locationMembership.membership',
  ])

  const fetchMemberships = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true)
        if (id) {
          const result = await Services.ClientLocationUserProfileMembershipService.get(locationName, id, [
            'locationMembership.membership',
            'locationMembership.prices',
            'locationMembership.benefits.benefitOffers.offer',
          ])

          if (result) {
            setLupMemberships(result)
          }
        }
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        captureError(error as Error)
      }
    },
    [locationName]
  )

  useEffect(() => {
    if (!lup.isLoading) {
      fetchMemberships(lup.data?.locationUserProfileMemberships?.[0]?.id || '')
    }
  }, [fetchMemberships, lup.data?.locationUserProfileMemberships, lup.isLoading])

  const renderMembership = (lupmembership: Types.LocationUserProfileMembershipClientDto | null) => {
    if (!lupmembership || lupmembership?.subStatus === 'CANCELED') {
      return (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="h5" color="text.secondaryShade60">
            {t('profile.membership.noMembership')}
          </Typography>
        </Paper>
      )
    }

    const { locationMembership, startDate, nextActionDate } = lupmembership
    const startDateUTC = getTzDateInUtc(startDate, null, location?.timezone || DEFAULT_TIMEZONE)
    const nextActionDateUTC = getTzDateInUtc(nextActionDate, null, location?.timezone || DEFAULT_TIMEZONE)
    return (
      <Stack spacing={2}>
        <Paper key={lupmembership?.id} elevation={1} sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center">
              <Typography variant="h5">{locationMembership?.membership?.displayName},&nbsp;</Typography>
              <Typography variant="h5" color="text.secondaryShade60">
                {locationMembership?.membership?.billingCycle &&
                  t(PRICE_PER_CYCLE_TEXT[locationMembership?.membership?.billingCycle as MembershipType], {
                    price: formatNumberAsPrice(locationMembership?.prices?.[0]?.value || 0),
                  })}
              </Typography>
            </Stack>
            <ColorChip
              label={t('global.label.active').toUpperCase()}
              textColor={theme.palette.success.main}
              bgColor={theme.palette.success.shade12}
              sxOverrides={{ borderRadius: 1, fontWeight: 600 }}
            />
          </Stack>
          <Spacer height={2} />
          <Stack spacing={1}>
            <BenefitsList benefits={locationMembership?.benefits || []} />
            {nextActionDate && (
              <Typography variant="body1">
                {t('global.label.nextPaymentDate', {
                  date: formatInTimeZone(nextActionDateUTC, location?.timezone || DEFAULT_TIMEZONE, 'MMM d, yyyy'),
                })}
              </Typography>
            )}
            <Typography>
              {t('membership.memberSince', {
                date: startDate
                  ? formatInTimeZone(startDateUTC, location?.timezone || DEFAULT_TIMEZONE, 'MMM, yyyy')
                  : '',
              })}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Notification lupId={sessionCtx.state.user?.lupId || ''} />

      <Typography variant="h2" pl={isBelowMD ? 2 : 0}>
        {t('profile.membership.title')}
      </Typography>
      <Spacer height={2} />
      <Typography variant="body1" pl={isBelowMD ? 2 : 0}>
        {t('profile.membership.subTitle')}
      </Typography>
      <Spacer height={2} />
      {lup.isLoading || isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={200} />
      ) : (
        <>
          {renderMembership(lupMemberships)}
          {lupMemberships?.subStatus === 'CANCELED' ||
          !isUnlimitedBenefitsOnly(lupMemberships?.locationMembership?.benefits || []) ? (
            <Paper elevation={1} sx={{ mt: 2 }}>
              <BenefitConsumptionTable
                locationUserProfile={lup.data as Types.LocationUserProfileClientDto}
                onActionClick={(benefitConsumptionRecord: Types.BenefitConsumptionRecordAdminDto) => {
                  navigateRoute('purchaseSummary', {
                    bookingId: benefitConsumptionRecord.invoiceItem?.invoice?.orderId || '',
                  })
                }}
                onServiceFetch={async ({ locationUserProfileId, options }) => {
                  return await Services.ClientBenefitConsumptionRecordsService.get(
                    locationName,
                    locationUserProfileId,
                    options
                  )
                }}
                hideFilters
              />
            </Paper>
          ) : null}
        </>
      )}
    </Box>
  )
}
